import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { getAdminTransaction, getAdminTransactions } from '../../api';
import {
  PageHeader, StatusBadge, Table, Pagination, SearchInput, EmptyState, TableSkeleton,
  UserCell, AstrologerCell,
} from '../../components/ui';
import Modal from '../../components/ui/Modal';
import OverviewBar from '../../components/ui/OverviewBar';
import ExportButton from '../../components/ui/ExportButton';
import { formatCurrency, formatDate, formatDateTime } from '../../utils/format';
import {
  Wallet, TrendingUp, TrendingDown, ArrowUpCircle, ArrowDownCircle,
  RefreshCw, Filter, X, Eye,
} from 'lucide-react';

const CATEGORIES = [
  'recharge', 'consultation', 'appointment', 'refund', 'promo_credit', 'earning',
  'commission', 'withdrawal', 'payout', 'coupon', 'admin_credit', 'admin_debit',
  'subscription', 'emergency', 'tax', 'gateway_fee',
];
const PROVIDERS = ['razorpay', 'manual', 'wallet', 'hybrid', 'none'];
const LIMIT = 20;

function PartyCell({ tx }: { tx: any }) {
  const entity = tx.party || { _id: tx.userId || tx.partyId };
  if (tx.userType === 'astrologer') {
    return (
      <div>
        <AstrologerCell entity={entity} />
        <span className="badge badge-gray text-[10px] mt-0.5">Astrologer</span>
      </div>
    );
  }
  return (
    <div>
      <UserCell entity={entity} />
      <span className="badge badge-gray text-[10px] mt-0.5">User</span>
    </div>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-2 py-2 border-b border-gray-50 last:border-0">
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="col-span-2 text-xs text-gray-900 break-all">{children}</dd>
    </div>
  );
}

export default function FinanceWalletPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [detailId, setDetailId] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin-transactions', page, search, typeFilter, categoryFilter, userTypeFilter, statusFilter, providerFilter, dateFrom, dateTo],
    queryFn: () =>
      getAdminTransactions({
        page,
        limit: LIMIT,
        search: search || undefined,
        type: typeFilter || undefined,
        category: categoryFilter || undefined,
        userType: userTypeFilter || undefined,
        status: statusFilter || undefined,
        provider: providerFilter || undefined,
        from: dateFrom || undefined,
        to: dateTo || undefined,
      }).then((r) => r.data),
    placeholderData: (prev) => prev,
    retry: 1,
  });

  const { data: detailData, isLoading: detailLoading } = useQuery({
    queryKey: ['admin-transaction', detailId],
    queryFn: () => getAdminTransaction(detailId!).then((r) => r.data.data),
    enabled: !!detailId,
  });

  const transactions = data?.data || [];
  const pagination = data?.pagination;
  const meta = data?.meta || {};

  const stats = [
    { label: 'Total TXNs', value: pagination?.total?.toLocaleString() ?? '—', icon: <Wallet size={14} /> },
    { label: 'Credits', value: meta.creditCount?.toLocaleString?.() ?? '—', icon: <ArrowUpCircle size={14} />, color: 'text-green-600' },
    { label: 'Debits', value: meta.debitCount?.toLocaleString?.() ?? '—', icon: <ArrowDownCircle size={14} />, color: 'text-red-600' },
    { label: 'Total In', value: formatCurrency(meta.creditTotal || 0), icon: <TrendingUp size={14} />, color: 'text-green-600' },
    { label: 'Total Out', value: formatCurrency(meta.debitTotal || 0), icon: <TrendingDown size={14} />, color: 'text-red-600' },
    {
      label: 'Net',
      value: formatCurrency((meta.creditTotal || 0) - (meta.debitTotal || 0)),
      color: (meta.creditTotal || 0) >= (meta.debitTotal || 0) ? 'text-green-600' : 'text-red-600',
    },
    { label: 'Bonus issued', value: formatCurrency(meta.bonusTotal || 0), color: 'text-emerald-600' },
    { label: 'Tax (on recharges)', value: formatCurrency(meta.taxTotal || 0), color: 'text-amber-700' },
    { label: 'Gateway fees', value: formatCurrency(meta.gatewayFeeTotal || 0), color: 'text-violet-700' },
  ];

  const EXPORT_COLS = [
    { key: '_id', label: 'ID' },
    { key: 'type', label: 'Type' },
    { key: 'category', label: 'Category' },
    { key: 'amount', label: 'Amount (paise)' },
    { key: 'taxAmount', label: 'Tax (paise)' },
    { key: 'gatewayFeeAmount', label: 'Gateway fee (paise)' },
    { key: 'paymentProvider', label: 'Provider' },
    { key: 'balanceBefore', label: 'Balance Before' },
    { key: 'balanceAfter', label: 'Balance After' },
    { key: 'description', label: 'Description' },
    { key: 'userType', label: 'User Type' },
    { key: 'status', label: 'Status' },
    { key: 'createdAt', label: 'Date' },
  ];

  const clearFilters = () => {
    setTypeFilter('');
    setCategoryFilter('');
    setUserTypeFilter('');
    setStatusFilter('');
    setProviderFilter('');
    setDateFrom('');
    setDateTo('');
    setSearch('');
    setPage(1);
  };
  const hasFilters = !!(typeFilter || categoryFilter || userTypeFilter || statusFilter || providerFilter || dateFrom || dateTo || search);

  const tx = detailData?.transaction;
  const ref = detailData?.reference;

  return (
    <div className="p-6">
      <PageHeader title="Transactions" subtitle="PDF §28 ledger — every wallet movement, consultation, appointment and withdrawal">
        <ExportButton data={transactions} filename="transactions" columns={EXPORT_COLS} />
        <button onClick={() => qc.invalidateQueries({ queryKey: ['admin-transactions'] })} className="btn-secondary btn-sm">
          <RefreshCw size={14} />
        </button>
      </PageHeader>

      <OverviewBar items={stats} loading={isLoading} />

      {isError ? (
        <div className="card p-6 mb-5 text-center">
          <p className="text-sm text-red-600 font-medium">Could not load wallet transactions</p>
          <p className="text-xs text-gray-500 mt-1">{(error as any)?.response?.data?.message || (error as Error)?.message || 'Request failed'}</p>
          <button type="button" className="btn-primary btn-sm mt-3" onClick={() => refetch()}>Retry</button>
        </div>
      ) : null}

      {Array.isArray(meta.byCategory) && meta.byCategory.length > 0 ? (
        <div className="card p-3 mb-5">
          <p className="text-xs font-semibold text-gray-700 mb-2">Breakdown by category (current filters)</p>
          <div className="flex flex-wrap gap-2">
            {meta.byCategory.slice(0, 16).map((row: any) => (
              <button
                key={String(row._id)}
                type="button"
                onClick={() => { setCategoryFilter(String(row._id || '')); setPage(1); }}
                className={`badge text-[10px] capitalize ${categoryFilter === row._id ? 'badge-green' : 'badge-gray'}`}
              >
                {String(row._id || 'other').replace(/_/g, ' ')} · {formatCurrency(row.total || 0)} ({row.count})
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="card p-4 mb-5">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[200px]">
            <SearchInput
              value={search}
              onChange={(v) => { setSearch(v); setPage(1); }}
              placeholder="Search name, phone, description, reference…"
            />
          </div>
          <div className="flex gap-1.5">
            {([['', 'All'], ['credit', 'Credits'], ['debit', 'Debits']] as const).map(([v, l]) => (
              <button
                key={v}
                onClick={() => { setTypeFilter(v); setPage(1); }}
                className={`px-3 py-1.5 rounded-lg border text-xs font-medium ${typeFilter === v ? 'bg-primary-500 text-white border-primary-500' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
              >
                {l}
              </button>
            ))}
          </div>
          <button onClick={() => setShowFilters(!showFilters)} className={`btn-secondary btn-sm ${showFilters ? 'bg-gray-100' : ''}`}>
            <Filter size={14} />
          </button>
          {hasFilters && (
            <button onClick={clearFilters} className="text-xs text-red-500 hover:underline flex items-center gap-1">
              <X size={12} />Clear
            </button>
          )}
        </div>

        {showFilters && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-3 pt-3 border-t border-gray-100">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
              <select className="input" value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}>
                <option value="">All</option>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c} className="capitalize">{c.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Provider</label>
              <select className="input" value={providerFilter} onChange={(e) => { setProviderFilter(e.target.value); setPage(1); }}>
                <option value="">All</option>
                {PROVIDERS.map((p) => (
                  <option key={p} value={p} className="capitalize">{p}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Party</label>
              <select className="input" value={userTypeFilter} onChange={(e) => { setUserTypeFilter(e.target.value); setPage(1); }}>
                <option value="">All</option>
                <option value="user">Users</option>
                <option value="astrologer">Astrologers</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select className="input" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
                <option value="">All</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
              <input type="date" className="input" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
              <input type="date" className="input" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} />
            </div>
          </div>
        )}
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-5"><TableSkeleton rows={10} cols={9} /></div>
        ) : transactions.length === 0 ? (
          <EmptyState message="No transactions found" icon={<Wallet size={32} />} />
        ) : (
          <Table>
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="table-th">#</th>
                <th className="table-th">Party</th>
                <th className="table-th">Type</th>
                <th className="table-th">Category</th>
                <th className="table-th">Provider</th>
                <th className="table-th">Description</th>
                <th className="table-th">Amount</th>
                <th className="table-th">Tax / Fee</th>
                <th className="table-th">Balance</th>
                <th className="table-th">Status</th>
                <th className="table-th">Date</th>
                <th className="table-th"></th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((t: any, idx: number) => (
                <tr key={t._id} className="table-row cursor-pointer" onClick={() => setDetailId(t._id)}>
                  <td className="table-td text-xs text-gray-400">{(page - 1) * LIMIT + idx + 1}</td>
                  <td className="table-td" onClick={(e) => e.stopPropagation()}><PartyCell tx={t} /></td>
                  <td className="table-td">
                    <div className={`flex items-center gap-1.5 text-xs font-semibold ${t.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                      {t.type === 'credit' ? <ArrowUpCircle size={13} /> : <ArrowDownCircle size={13} />}
                      {t.type}
                    </div>
                  </td>
                  <td className="table-td">
                    <span className="badge badge-gray capitalize">{t.category?.replace(/_/g, ' ')}</span>
                  </td>
                  <td className="table-td text-xs capitalize text-gray-600">{t.paymentProvider || '—'}</td>
                  <td className="table-td text-xs max-w-[220px]">
                    <p className="truncate" title={t.description}>{t.description}</p>
                    {t.reference ? (
                      <p className="text-[10px] text-gray-400 truncate mt-0.5">Ref: {t.reference}</p>
                    ) : null}
                  </td>
                  <td className="table-td">
                    <span className={`text-xs font-bold ${t.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                      {t.type === 'credit' ? '+' : '-'}{formatCurrency(t.amount)}
                    </span>
                  </td>
                  <td className="table-td text-[11px] text-gray-500 whitespace-nowrap">
                    {(t.taxAmount || t.gatewayFeeAmount) ? (
                      <>
                        {t.taxAmount ? <div>Tax {formatCurrency(t.taxAmount)}</div> : null}
                        {t.gatewayFeeAmount ? <div>Fee {formatCurrency(t.gatewayFeeAmount)}</div> : null}
                      </>
                    ) : '—'}
                  </td>
                  <td className="table-td text-xs">
                    <div className="font-semibold">{formatCurrency(t.balanceAfter)}</div>
                    <div className="text-[10px] text-gray-400">from {formatCurrency(t.balanceBefore)}</div>
                  </td>
                  <td className="table-td"><StatusBadge status={t.status || 'completed'} /></td>
                  <td className="table-td text-xs whitespace-nowrap">{formatDate(t.createdAt)}</td>
                  <td className="table-td">
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={(e) => { e.stopPropagation(); setDetailId(t._id); }}
                    >
                      <Eye size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {pagination?.pages > 1 && (
          <div className="p-4 border-t border-gray-100">
            <Pagination page={page} pages={pagination.pages} total={pagination.total} limit={LIMIT} onPageChange={setPage} />
          </div>
        )}
      </div>

      <Modal open={!!detailId} onClose={() => setDetailId(null)} title="Transaction details" size="lg">
        {detailLoading || !tx ? (
          <div className="py-8 text-center text-sm text-gray-500">Loading…</div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 items-center">
              <StatusBadge status={tx.status || 'completed'} />
              <span className={`badge ${tx.type === 'credit' ? 'badge-green' : 'badge-red'} capitalize`}>{tx.type}</span>
              <span className="badge badge-gray capitalize">{tx.category?.replace(/_/g, ' ')}</span>
              <span className="text-lg font-bold ml-auto">{tx.type === 'credit' ? '+' : '-'}{formatCurrency(tx.amount)}</span>
            </div>

            <dl>
              <DetailRow label="Party">
                {tx.userType === 'astrologer' ? (
                  <AstrologerCell entity={tx.party || { _id: tx.userId }} />
                ) : (
                  <UserCell entity={tx.party || { _id: tx.userId }} />
                )}
              </DetailRow>
              <DetailRow label="Description">{tx.description || '—'}</DetailRow>
              <DetailRow label="Balance before">{formatCurrency(tx.balanceBefore)}</DetailRow>
              <DetailRow label="Balance after">{formatCurrency(tx.balanceAfter)}</DetailRow>
              <DetailRow label="Reference">{tx.reference || '—'}</DetailRow>
              <DetailRow label="Reference type">{tx.referenceType || '—'}</DetailRow>
              <DetailRow label="Payment provider"><span className="capitalize">{tx.paymentProvider || '—'}</span></DetailRow>
              <DetailRow label="Tax / GST">{formatCurrency(tx.taxAmount || 0)}</DetailRow>
              <DetailRow label="Gateway fee">{formatCurrency(tx.gatewayFeeAmount || 0)}</DetailRow>
              <DetailRow label="TXN ID"><span className="font-mono">{tx._id}</span></DetailRow>
              <DetailRow label="Wallet ID"><span className="font-mono">{tx.walletId || '—'}</span></DetailRow>
              <DetailRow label="Created">{formatDateTime(tx.createdAt)}</DetailRow>
            </dl>

            {ref ? (
              <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                <p className="text-xs font-semibold text-gray-700 mb-2">Linked {tx.referenceType}</p>
                {tx.referenceType === 'consultation' && (
                  <div className="text-xs space-y-1">
                    <p>Type: <span className="capitalize">{ref.type}</span> · Status: {ref.status}</p>
                    <p>Amount: {formatCurrency(ref.totalAmount || 0)} · Astro earnings: {formatCurrency(ref.astrologerEarnings || 0)}</p>
                    <div className="flex gap-4 mt-2" onClick={(e) => e.stopPropagation()}>
                      <UserCell entity={ref.userId} />
                      <AstrologerCell entity={ref.astrologerId} />
                    </div>
                    {ref._id ? (
                      <Link to={`/consultations/${ref._id}`} className="text-primary-600 hover:underline text-xs mt-2 inline-block">
                        Open consultation
                      </Link>
                    ) : null}
                  </div>
                )}
                {tx.referenceType === 'order' && (
                  <div className="text-xs space-y-1">
                    <p>Order #{ref.orderNumber || String(ref._id).slice(-6)}</p>
                    <p>Total: {formatCurrency(ref.totalAmount || 0)} · Status: {ref.orderStatus}</p>
                    <UserCell entity={ref.userId} />
                  </div>
                )}
                {tx.referenceType === 'payment' && (
                  <div className="text-xs space-y-1">
                    <p>Payment {ref.status} · {formatCurrency(ref.amount || 0)} · <span className="capitalize">{ref.provider}</span></p>
                    <p>Tax {formatCurrency(ref.taxAmount || 0)} · Fee {formatCurrency(ref.gatewayFeeAmount || 0)} · Subtotal {formatCurrency(ref.subtotalAmount || 0)}</p>
                    <p className="font-mono text-[10px] text-gray-500">{ref.providerPaymentId || ref.providerOrderId}</p>
                    <UserCell entity={ref.userId} />
                    <Link to="/finance/payments" className="text-primary-600 hover:underline text-xs mt-2 inline-block">
                      Open payments
                    </Link>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        )}
      </Modal>
    </div>
  );
}
